import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { TagModule } from 'primeng/tag';
import type {
  TableColumn,
  PaginationEvent,
} from '@shared/components/data-table/data-table.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import { SearchFormComponent } from '@shared/components/search-form/search-form.component';
import { OrderChangeHistory } from '@app/api/order-service/model/orderChangeHistory';
import { O2OOrderService } from '../services/o2o-order.service';
import {
  O2O_CHANGE_HISTORY_FIELD_ALL,
  O2O_CHANGE_HISTORY_SEARCH_CONFIG,
  O2O_CHANGE_HISTORY_TABLE_COLUMNS,
  O2oChangeHistorySearchParams,
} from './o2o-change-history.config';
import {
  changeHistoryNoteSeverity,
  formatChangeHistoryDateTime,
  formatChangeHistoryNote,
  formatChangeHistoryValue,
} from './o2o-change-history.display';

@Component({
  selector: 'app-o2o-change-history',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ToastModule,
    TagModule,
    AdvancedDataTableComponent,
    SearchFormComponent,
  ],
  providers: [MessageService],
  templateUrl: './o2o-change-history.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class O2OChangeHistoryComponent implements OnInit {
  private readonly o2oOrderService = inject(O2OOrderService);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);

  readonly searchConfig = O2O_CHANGE_HISTORY_SEARCH_CONFIG;
  readonly columns: TableColumn[] = O2O_CHANGE_HISTORY_TABLE_COLUMNS;
  readonly searchPreset = { fieldName: 'deliveryRetry' };

  rows = signal<OrderChangeHistory[]>([]);
  loading = signal(false);
  totalRecords = signal(0);
  currentPage = signal(1);
  pageSize = signal(20);

  private currentFilters: O2oChangeHistorySearchParams = {
    fieldName: 'deliveryRetry',
  };

  ngOnInit(): void {
    this.loadData();
  }

  onSearch(criteria: O2oChangeHistorySearchParams): void {
    const fieldName =
      criteria.fieldName === undefined || criteria.fieldName === null
        ? 'deliveryRetry'
        : criteria.fieldName === O2O_CHANGE_HISTORY_FIELD_ALL ||
            criteria.fieldName === ''
          ? undefined
          : criteria.fieldName;

    this.currentFilters = {
      innerOrderNo: criteria.innerOrderNo || undefined,
      outerOrderNo: criteria.outerOrderNo || undefined,
      fieldName,
      dateFrom: criteria.dateFrom
        ? new Date(criteria.dateFrom).toISOString()
        : undefined,
      dateTo: criteria.dateTo
        ? new Date(criteria.dateTo).toISOString()
        : undefined,
    };
    this.currentPage.set(1);
    this.loadData();
  }

  onReset(): void {
    this.currentFilters = { fieldName: 'deliveryRetry' };
    this.currentPage.set(1);
    this.loadData();
  }

  onPageChange(event: PaginationEvent): void {
    this.currentPage.set((event.page ?? 0) + 1);
    this.pageSize.set(event.rows ?? this.pageSize());
    this.loadData();
  }

  openOrder(row: OrderChangeHistory): void {
    if (row.orderId) {
      void this.router.navigate(['/o2o/view', row.orderId]);
      return;
    }
    if (row.innerOrderNo) {
      void this.router.navigate(['/o2o/internal'], {
        queryParams: { orderNo: row.innerOrderNo },
      });
    }
  }

  formatDateTime = formatChangeHistoryDateTime;

  formatNote(raw?: string): string {
    return formatChangeHistoryNote(raw, this.translate);
  }

  formatChangeValue(raw?: string): string {
    return formatChangeHistoryValue(raw, this.translate);
  }

  noteSeverity = changeHistoryNoteSeverity;

  private loadData(): void {
    this.loading.set(true);
    this.o2oOrderService
      .listOrderChangeHistory({
        innerOrderNo: this.currentFilters.innerOrderNo,
        outerOrderNo: this.currentFilters.outerOrderNo,
        fieldName: this.currentFilters.fieldName,
        fromDate: this.currentFilters.dateFrom,
        toDate: this.currentFilters.dateTo,
        page: this.currentPage() - 1,
        size: this.pageSize(),
      })
      .subscribe({
        next: (page) => {
          this.rows.set(page?.pageContent ?? []);
          this.totalRecords.set(page?.totalElements ?? 0);
          this.loading.set(false);
        },
        error: () => {
          this.rows.set([]);
          this.totalRecords.set(0);
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('common.error'),
            detail: this.translate.instant('o2o.change_history.load_failed'),
          });
        },
      });
  }
}
